import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Dropdown, Menu, Switch, Tooltip, Modal } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import uuid from 'uuid';

import { Container, TitleWrapper, ButtonAsync, Input, Text, IconWithText, IconWrapper, BoxWrapper } from '../design';
import icDelete from 'utils/assetFiles/svgs/icDelete';
import icMenu from 'utils/assetFiles/svgs/icMenu';
import icPen from 'utils/assetFiles/svgs/icPen';
import icEdit from 'utils/assetFiles/svgs/icEdit';
import icPlus from 'utils/assetFiles/svgs/icPlus';
import icPlusOff from 'utils/assetFiles/svgs/icPlusOff';

import adminRequests from 'services/admin';
import EditInstructor from './EditInstructor';
import { generatePassword } from '../helper';
import { PageContext } from '../PageProvider';
import { message } from 'shell/components';

const HeaderWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr) 60px 1fr;
    border-bottom: 1px solid #eeeeee;
    padding-bottom: 10px;
`;

const RowWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr) 60px 1fr;
    border-bottom: 1px solid #eeeeee;
    padding: 10px 0px;
    :hover {
        background-color: #efedff;
        border-radius: 6px;
    }
`;

const HeaderItem = styled(Text)`
    color: #757575;
`;

const InputGridWrapper = styled.div`
    display: grid;
    grid-template-rows: repeat(2, auto);
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 30px;
    grid-row-gap: 10px;
`;

const SubmitBtnWrapper = styled.div`
    grid-row: 1/3;
    grid-column: 3/4;
    align-self: end;
    justify-self: end;
    display: flex;
    align-items: center;
`;

const Cell = styled(Text)``;

const CellEnd = styled.div`
    justify-self: end;
    padding: 0px 8px;
`;

const OptionWrapper = styled.div`
    float: right;
    padding: 0px 5px;
    border-radius: 3px;
    :hover {
        background: #c0c0c0;
    }
`;

const InviteInstructor = ({ complete, onSubmit, onEdit }) => {
    const { reviewData, setReviewData } = useContext(PageContext);

    const [addDisabled, setAddDisabled] = useState(true);
    const [instructorModal, setInstructorModal] = useState(false);
    const [editData, setEditData] = useState({});

    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [autogen, setAutogen] = useState(true);

    const handleClickAddPerson = async () => {
        const { students = [], instructors = [] } = reviewData;
        const newUsers = [].concat(instructors, students);
        const data = { id: uuid.v4(), name, username, password };
        const strUsername = String(data.username).toLowerCase();

        if (newUsers.find((item) => item.username === strUsername)) {
            message.error('Username already exists in the list');
            return;
        }

        const user = await adminRequests.findUser({ username: strUsername });
        const newPerson = [];

        if (user) {
            newPerson.push({
                id: user.id,
                name: user.name,
                username: user.username,
                record: true,
            });
        } else {
            newPerson.push({
                id: data.id,
                name: data.name,
                username: strUsername,
                password: autogen ? generatePassword() : data.password,
            });
        }

        const newInstructors = [].concat(instructors, newPerson);
        setReviewData((prev) => ({ ...prev, instructors: newInstructors }));

        setName('');
        setUsername('');
        setPassword('');
    };

    const handleSaveInstructor = async (data) => {
        const { students = [], instructors = [] } = reviewData;
        const newUsers = [].concat(instructors, students);
        const strUsername = String(data.username).toLowerCase();
        const duplUsername = newUsers.find((item) => item.username === strUsername && item.id !== data.id);

        if (duplUsername) {
            message.error('Username already exists in the list');
            return;
        }

        // const user = await postRequest(ADMIN.FIND_USER, { username: strUsername });
        const user = await adminRequests.findUser({ username: strUsername });
        const newInstructors = instructors.map((item) => {
            if (item.id === data.id) {
                return user
                    ? {
                          id: data.id,
                          name: user.name,
                          username: user.username,
                          record: true,
                      }
                    : {
                          id: data.id,
                          name: data.name,
                          username: strUsername,
                          password: data.password,
                      };
            }
            return item;
        });

        setReviewData((prev) => ({ ...prev, instructors: newInstructors }));
        setInstructorModal(false);
    };

    const handleEditInstructor = (data) => {
        setEditData(data);
        setInstructorModal(true);
    };

    const handleCancelEditInstructor = () => {
        setInstructorModal(false);
    };

    const handleClickDelete = (data) => {
        const { instructors } = reviewData;
        setReviewData((prev) => ({
            ...prev,
            instructors: instructors.filter((item) => {
                return item.id !== data.id;
            }),
        }));
    };

    const handleSubmit = () => {
        if (onSubmit) onSubmit();
    };

    const handleEditInviteInstructor = () => {
        if (onEdit) onEdit();
    };

    useEffect(() => {
        if (name && username && (password || autogen)) {
            setAddDisabled(false);
        } else {
            setAddDisabled(true);
        }
    }, [name, username, password, autogen]);

    return (
        <>
            <Container complete={complete}>
                <TitleWrapper complete={complete}>
                    Invite Instructor
                    {complete !== false && <IconWrapper onClick={handleEditInviteInstructor}>{icEdit}</IconWrapper>}
                </TitleWrapper>

                <BoxWrapper complete={complete}>
                    {reviewData.instructors.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <HeaderWrapper>
                                <HeaderItem>Name</HeaderItem>
                                <HeaderItem>Username</HeaderItem>
                                <HeaderItem>Password</HeaderItem>
                                <HeaderItem>Record</HeaderItem>
                            </HeaderWrapper>
                            {reviewData.instructors.map((i) => (
                                <RowWrapper key={i.username}>
                                    <Cell>{i.name}</Cell>
                                    <Cell>{i.username}</Cell>
                                    <Cell>{i.password}</Cell>
                                    <Cell>
                                        {i.record && (
                                            <Tooltip title='This user already has an Ensightful account'>
                                                <WarningOutlined
                                                    style={{
                                                        color: 'orange',
                                                        fontSize: 16,
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Cell>
                                    <CellEnd>
                                        <Dropdown
                                            overlay={
                                                <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
                                                    {!i.record && (
                                                        <Menu.Item onClick={() => handleEditInstructor(i)}>
                                                            <span style={{ paddingRight: 10 }}>{icPen}</span>
                                                            <span>Edit</span>
                                                        </Menu.Item>
                                                    )}
                                                    <Menu.Item onClick={() => handleClickDelete(i)}>
                                                        <span style={{ paddingRight: 10 }}>{icDelete}</span>
                                                        <span>Delete</span>
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                            placement='bottomRight'
                                            trigger={['click']}
                                        >
                                            <OptionWrapper onClick={(e) => e.stopPropagation()}>{icMenu}</OptionWrapper>
                                        </Dropdown>
                                    </CellEnd>
                                </RowWrapper>
                            ))}
                        </div>
                    )}
                    <InputGridWrapper>
                        <div>
                            <Text style={{ marginBottom: 5 }} required>
                                NAME
                            </Text>
                            <Input id={'au_instructorName'} value={name} onChange={(e) => setName(e.target.value)} />
                        </div>
                        <div>
                            <Text style={{ marginBottom: 5 }} required>
                                USERNAME
                            </Text>
                            <Input
                                id={'au_instructorUsername'}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>
                        <div>
                            <Text style={{ marginBottom: 5 }} disabled={autogen} required={!autogen}>
                                PASSWORD
                            </Text>
                            <Input
                                id={'au_instructorPassword'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={autogen}
                            />
                        </div>
                        <div style={{ alignSelf: 'end', lineHeight: '30px' }}>
                            <Switch
                                id={'au_autogenSwitch'}
                                defaultChecked
                                onChange={() => setAutogen(!autogen)}
                                style={{ marginRight: 5 }}
                            />
                            AutoGen Password
                        </div>
                        <SubmitBtnWrapper>
                            <IconWithText
                                id={'au_addInstructor'}
                                disabled={addDisabled}
                                icon={addDisabled ? icPlusOff : icPlus}
                                text='Add'
                                onClick={!addDisabled ? handleClickAddPerson : null}
                                textColor={addDisabled && '#757575'}
                            />
                            <ButtonAsync
                                id={'au_instructorNext'}
                                disabled={reviewData.instructors.length === 0}
                                onClick={handleSubmit}
                            />
                        </SubmitBtnWrapper>
                    </InputGridWrapper>
                </BoxWrapper>
            </Container>

            <Modal
                centered
                key={Math.random()}
                visible={instructorModal}
                onCancel={handleCancelEditInstructor}
                footer={null}
            >
                <EditInstructor data={editData} onSave={handleSaveInstructor} onCancel={handleCancelEditInstructor} />
            </Modal>
        </>
    );
};

export default InviteInstructor;
